import { yupResolver } from "@hookform/resolvers/yup";
import { useSnackbar } from "notistack";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router";

import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Grid from "@mui/material/Grid";

import Copyright from "../../../components/Copyright";
import ControlledTextField from "../../../components/controlled/ControlledTextField";

import { useGetCurrentBrandingQuery } from "../../branding/brandingApiSlice";
import { useAuth } from "../AuthContext";

import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import LoginBackground from "../../../components/LoginBackground";
import Logo from "../../../components/Logo";
import { getFormSchema } from "./formSchema";

export default function SignIn() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { enqueueSnackbar } = useSnackbar();
	const { user, signIn, isSigningIn } = useAuth();
	const { data: branding, isLoading } = useGetCurrentBrandingQuery();

	const formSchema = getFormSchema(t);
	const {
		handleSubmit,
		control,
		formState: { errors },
		setError,
	} = useForm({
		resolver: yupResolver(formSchema),
		shouldFocusError: true,
	});

	const onSubmit = async ({ email, password }) => {
		try {
			const user = await signIn(email, password);
			setTimeout(() => {
				const navigateTo = user.role === "super-admin" ? "/admin" : "/";
				navigate(navigateTo);
			}, 300);
		} catch (error) {
			// console.log("Error during sign in:", error);
			if (error?.data?.errors) {
				const errors = error.data.errors;
				Object.entries(errors).forEach(([key, message]) => {
					setError(key);
					enqueueSnackbar(message[0], {
						variant: "error",
						preventDuplicate: true,
					});
				});
			} else {
				enqueueSnackbar(error?.data?.message || t("default_error"), {
					variant: "error",
					preventDuplicate: true,
				});
			}
		}
	};

	// if (isLoading) return <FullLoadingScreen />;

	const shouldUseBrandedTheme =
		branding?.loginBoxColor ||
		branding?.loginBgColor ||
		branding?.barColor ||
		branding?.buttonColor;

	return (
		<Grid container component="main" sx={{ height: "100vh" }}>
			<CssBaseline />
			<LoginBackground>
				<Box>
					<Box sx={{ display: "flex", justifyContent: "center", mb: 5 }}>
						<Logo />
					</Box>
					<Box
						component="form"
						noValidate
						onSubmit={handleSubmit(onSubmit)}
						sx={{
							pt: 5,
							pb: 7,
							px: 6,
							borderRadius: 0,
							background: (theme) => theme.palette.loginBox,
						}}
					>
						<ControlledTextField
							sx={{
								"& .MuiInputBase-root": { background: "#fff !important" },
								"& .MuiFormHelperText-root.Mui-error": { color: "#fff" },
							}}
							shrinkLabel={false}
							variant="filled"
							margin="normal"
							control={control}
							name="email"
							label={t("email_label")}
							error={!!errors?.email}
							defaultValue=""
							autoFocus
							type="email"
							required
						/>
						<ControlledTextField
							sx={{ "& .MuiInputBase-root": { background: "#fff !important" } }}
							shrinkLabel={false}
							variant="filled"
							margin="normal"
							control={control}
							name="password"
							label={t("password_label")}
							error={!!errors?.password}
							defaultValue=""
							type="password"
							autoComplete="password"
							required
						/>
						<Grid container>
							<Grid size="grow">
								<Link to="/forgot-password" style={{ color: "#fff" }}>
									{t("forgot_password")}
								</Link>
							</Grid>
							{!shouldUseBrandedTheme && (
								<Grid>
									<Link to="/register" style={{ color: "#fff" }}>
										{t("create_account")}
									</Link>
								</Grid>
							)}
						</Grid>
						<Box sx={{ display: "flex", justifyContent: "center" }}>
							<Button
								loading={isSigningIn}
								type="submit"
								variant="contained"
								sx={{ mt: 2 }}
							>
								{t("sign_in")}
							</Button>
						</Box>
					</Box>
				</Box>
				<Copyright sx={{ mt: 8, mb: 4 }} />
			</LoginBackground>
		</Grid>
	);
}
